import { useShareDialogStore } from "../store/share-dialog-store";
import {
  useGetShareLink,
  useRemoveShareToken,
} from "@/api/hooks/use-share-chat-api";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/api/config/query-keys";

/**
 * useShareChat Hook
 *
 * Zero Props Pattern: API + Store Integration
 * Component'lar bu hook'u kullanır, hiç prop almaz
 */
export const useShareChat = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  const extractShareToken = (link: string): string | null => {
    if (!link) return null;
    try {
      const url = new URL(link);
      return url.searchParams.get("token");
    } catch {
      const match = link.match(/[?&]token=([^&]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    }
  };

  const updateConversationListCache = (
    conversationIdToUpdate: string,
    patch: Partial<{ isPublic: boolean; shareToken: string | null }>
  ) => {
    queryClient.setQueriesData(
      { queryKey: QueryKeys.CONVERSATIONS.list, exact: false },
      (oldData: any) => {
        // useInfiniteQuery cache shape: { pages: ConversationsListResponse[], pageParams: ... }
        if (!oldData?.pages || !Array.isArray(oldData.pages)) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => {
            const result = page?.data?.result;
            if (!Array.isArray(result)) return page;

            const nextResult = result.map((conv: any) => {
              if (conv?._id !== conversationIdToUpdate) return conv;
              return {
                ...conv,
                ...patch,
              };
            });

            return {
              ...page,
              data: {
                ...page.data,
                result: nextResult,
              },
            };
          }),
        };
      }
    );
  };

  const updateConversationMessagesCache = (
    conversationIdToUpdate: string,
    patch: Partial<{ isPublic: boolean }>
  ) => {
    // Hem normal messages query'sini hem de infinite variant'ını yakalamak için prefix key kullanıyoruz
    queryClient.setQueriesData(
      {
        queryKey: QueryKeys.CONVERSATIONS.messages(conversationIdToUpdate),
        exact: false,
      },
      (oldData: any) => {
        // InfiniteData<BackendInfiniteScrollResponse> shape: { pages: [...], pageParams: [...] }
        if (!oldData?.pages || !Array.isArray(oldData.pages)) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => {
            // BackendInfiniteScrollResponse: { data: { isPublic, ... } }
            if (!page?.data) return page;
            return {
              ...page,
              data: {
                ...page.data,
                ...(patch.isPublic !== undefined
                  ? { isPublic: patch.isPublic }
                  : null),
              },
            };
          }),
        };
      }
    );
  };

  // Store
  const {
    isOpen,
    shareLink,
    conversationId,
    isPublic,
    openDialog,
    setShareLink,
    setIsPublic,
    closeDialog,
    reset,
  } = useShareDialogStore();

  // API Hook
  const { mutateAsync: getShareLink, isPending } = useGetShareLink();
  const { mutateAsync: removeShareToken, isPending: isDisablePending } =
    useRemoveShareToken();

  // Actions
  const buildShareUrl = (chatId: string, shareToken: string) => {
    // Frontend base her ortamda değişebileceği için origin'den oluşturuyoruz
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/chat/${chatId}/?token=${shareToken}`;
  };

  const createShareLink = async (
    chatId: string,
    chatIsPublic: boolean,
    existingShareToken?: string | null
  ) => {
    try {
      // Dialog'u aç
      openDialog(chatId, chatIsPublic);

      // Eğer conversation'dan gelen bir shareToken varsa, direkt link'i üret
      if (existingShareToken) {
        const url = buildShareUrl(chatId, existingShareToken);
        setShareLink(url);
        setIsPublic(true);

        // Header badge'in stale kalmaması için messages cache'ini de senkronla
        updateConversationListCache(chatId, {
          isPublic: true,
          shareToken: existingShareToken,
        });
        updateConversationMessagesCache(chatId, { isPublic: true });

        // Sunucu ile de senkron kalalım
        await queryClient.invalidateQueries({
          queryKey: QueryKeys.CONVERSATIONS.messages(chatId),
          exact: false,
        });
        return;
      }

      // Aksi halde backend'den yeni share link iste
      const response = await getShareLink(chatId);
      const link = response.data.link;
      setShareLink(link);
      setIsPublic(true);
      success(t("chat.share.linkCreated") || "Link created successfully!");

      // 🚀 Optimistic cache update (UI anında güncellensin)
      const shareToken = extractShareToken(link);
      updateConversationListCache(chatId, {
        isPublic: true,
        shareToken,
      });
      updateConversationMessagesCache(chatId, { isPublic: true });

      // Sidebar'da ikonun anında görünmesi için conversations listesini yenile
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.CONVERSATIONS.list,
        exact: false,
      });

      // ChatHeader'ın dayandığı messages/infinite query'sini de yenile (staleTime 5dk)
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.CONVERSATIONS.messages(chatId),
        exact: false,
      });
    } catch (apiError: any) {
      console.error("Create share link error:", apiError);
      showError(
        apiError?.message || t("chat.share.error") || "Failed to create link"
      );
      closeDialog(); // Hata durumunda dialog'u kapat
    }
  };

  const closeAndResetDialog = () => {
    closeDialog();
    // Dialog kapatıldıktan 300ms sonra reset et (animation için)
    setTimeout(() => {
      reset();
    }, 300);
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
      } else {
        // Fallback for older browsers / restricted contexts
        const textarea = document.createElement("textarea");
        textarea.value = shareLink;
        textarea.style.position = "fixed";
        textarea.style.top = "-9999px";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      success(t("common.copiedToClipboard"));
    } catch (clipboardError: any) {
      console.error("Clipboard copy error:", clipboardError);
      showError(t("error.somethingWentWrong"));
    }
  };

  const handleDisableSharing = async () => {
    if (!conversationId) {
      showError(t("error.somethingWentWrong"));
      return;
    }

    try {
      const response = await removeShareToken(conversationId);

      success(
        t("chat.share.disabledSuccess") ||
          response?.message ||
          "Sharing disabled"
      );

      // 🚀 Optimistic cache update
      updateConversationListCache(conversationId, {
        isPublic: false,
        shareToken: null,
      });
      updateConversationMessagesCache(conversationId, { isPublic: false });

      // Conversations list'i invalidate et ki shareToken/isPublic güncellensin
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.CONVERSATIONS.list,
        exact: false,
      });

      // Messages cache'i de invalidate (header badge için)
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.CONVERSATIONS.messages(conversationId),
        exact: false,
      });

      // Dialog'u kapat + temizle
      setIsPublic(false);
      closeAndResetDialog();
    } catch (apiError: any) {
      console.error("Disable sharing error:", apiError);
      showError(
        apiError?.message || t("chat.share.disableError") || t("error.unknown")
      );
    }
  };

  const handleCloseDialog = () => {
    closeAndResetDialog();
  };

  return {
    // States
    isOpen,
    shareLink,
    conversationId,
    isPublic,
    isPending,
    isDisablePending,

    // Actions
    createShareLink,
    handleCopyLink,
    handleDisableSharing,
    closeDialog: handleCloseDialog,
  };
};
